import { NavLink } from "react-router-dom";
import { BookOpen, ClipboardList, LayoutDashboard, MessageCircle, Settings } from "lucide-react";
import { motion } from "framer-motion";
import { cn } from "@/lib/cn";

type Item = {
  to: string;
  label: string;
  icon: typeof BookOpen;
  end?: boolean;
};

const items: Item[] = [
  { to: "/dashboard", label: "Home",     icon: LayoutDashboard, end: true },
  { to: "/subjects",  label: "Subjects", icon: BookOpen },
  { to: "/chat",      label: "Tutor",    icon: MessageCircle },
  { to: "/tests",     label: "Tests",    icon: ClipboardList },
  { to: "/settings",  label: "More",     icon: Settings },
];

export function BottomNav() {
  return (
    <nav
      className={cn(
        "lg:hidden",
        "fixed inset-x-0 bottom-0 z-40",
        "border-t border-border bg-surface/95 backdrop-blur-md",
        "pb-safe",
      )}
      aria-label="Primary"
    >
      <ul className="flex h-16 items-stretch">
        {items.map(({ to, label, icon: Icon, end }) => (
          <li key={to} className="flex-1">
            <NavLink
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  "relative h-full flex flex-col items-center justify-center gap-1",
                  "text-[11px] font-medium leading-none select-none",
                  "transition-colors duration-150",
                  "active:bg-surface-2",
                  isActive ? "text-brand" : "text-text-muted hover:text-text",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {/* Active indicator. The motion.span itself spans the full
                      width of the nav item (inset-x-0, flex justify-center)
                      so it has no transform-based centering that framer-motion's
                      layout animation can clobber. The inner span is the
                      visible brand bar; centering happens via flex on the
                      outer span, not via translate. Framer-motion morphs the
                      outer span's bounding box between active items, smoothly. */}
                  {isActive ? (
                    <motion.span
                      aria-hidden
                      layoutId="bottom-nav-active"
                      className="pointer-events-none absolute top-0 inset-x-0 flex justify-center"
                      transition={{ type: "spring", stiffness: 420, damping: 32, mass: 0.6 }}
                    >
                      <span className="block h-0.5 w-8 rounded-full bg-brand" />
                    </motion.span>
                  ) : null}
                  <Icon
                    className={cn(
                      "h-5 w-5 transition-transform duration-150",
                      isActive && "scale-110",
                    )}
                  />
                  <span>{label}</span>
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
